"use client";

import React, { useEffect, useState } from "react";
import { NumberCard, NumberCardSkeleton } from "../Cards";
import { Hyphenated } from "@/utils/Hyphenated";
import GradientButton from "../Buttons/GradientButton";
import { useLiveNumbers } from "@/lib/useLiveNumbers";

export default function CardsGrid({
  heading,
  initialData = [],
}: {
  heading: string;
  initialData?: any[];
}) {
  const isPrivate = heading.includes("Private");
  const [numbers, setNumbers] = useState<any[]>(initialData);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      setNumbers(initialData);
      setLoading(false);
    }
  }, [initialData]);

  // Private section: overlay the build-time snapshot with the live inventory
  // (8 random currently-available numbers); static data is the fallback.
  const live = useLiveNumbers(isPrivate ? "all" : null);
  useEffect(() => {
    if (isPrivate && live?.privateNumbers?.length) {
      const shuffled = [...live.privateNumbers].sort(() => 0.5 - Math.random());
      setNumbers(shuffled.slice(0, 8));
    }
  }, [live, isPrivate]);

  if (loading) {
    return (
      <>
        <div className=" w-full h-auto pb-8">
          <h2 className="font-spaceGrotesk text-3xl font-bold text-zinc-800">
            {heading}
          </h2>
        </div>

        <div className="gap-4  w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: isPrivate ? 8 : 16 }).map((_, index) => (
            <NumberCardSkeleton key={index} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className=" w-full h-auto pb-8">
        <h2 className="font-spaceGrotesk text-3xl font-bold text-zinc-800">
          {heading}
        </h2>
      </div>

      <div className="gap-4  w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {error ? (
          <h2 className="w-screen font-spaceGrotesk text-3xl font-bold text-red-600">
            The data you are looking for is not available!
          </h2>
        ) : (
          numbers.map((number) => (
            <NumberCard
              isPrivate={isPrivate}
              key={number.id}
              country={`${number.countries.country_flag} ${number.countries.country_name}`}
              status={number.status.toLowerCase()}
              number={`+${number.countries.country_tel_code}-${number.number}`}
              href={
                isPrivate
                  ? `/account/rent-number?pn=${number.id}`
                  : `/free-temporary-numbers/${Hyphenated(
                      number?.countries?.country_name
                    )}/${number.countries.country_tel_code}${number?.number}`
              }
              price={isPrivate ? number.week_price ?? number.price : undefined}
            />
          ))
        )}
      </div>

      <div className="w-full py-10 flex items-center justify-center">
        <GradientButton
          href={isPrivate ? "/rent-private-number" : "/free-temporary-numbers"}
          text={
            isPrivate ? "View More Private Phone Numbers" : "View More Free Temporary Numbers"
          }
          textSize="text-lg"
          isRounded
        />
      </div>
    </>
  );
}
